import { useContext, useState } from "react";
import { Box, Tooltip } from "@mui/material";
import { DiscoveryContext } from "../discovery-context";
import {
  isCompositeIndexScoreType,
  getSentimentScoreByFactor,
  getOrderedAccessibilityFactors,
  getBrandPillarsDetail,
  filterCompositeFactors,
  isExcludedCompositeFactor,
  calcMultiFactorCompositeScore,
  calcDirectIndexScoreForEntity,
} from "../../../utils/brandIndexCalc";
import BrandAvatar from "../../../Component/Avatar/Avatar";
import MUISelect from "../../../Component/UI/MUISelect/MUISelect";
import { INDEX_ACCENT, INDEX_TRACK, INDEX_TEXT, INDEX_TEXT_MUTED } from "../../../utils/indexScoreTheme";
import classes from "./CompositeMetricsComparison.module.css";

// Helper to extract domain from URL
function extractDomain(input) {
  if (!input) return null;
  if (/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(input)) {
    return input;
  }
  try {
    if (!/^https?:\/\//i.test(input)) {
      input = "http://" + input;
    }
    const urlObject = new URL(input);
    let hostname = urlObject.hostname;
    const parts = hostname.split(".");
    if (parts.length > 2) {
      hostname = parts.slice(-2).join(".");
    }
    return hostname;
  } catch (error) {
    return null;
  }
}

const METRICS = ["visibility", "sentiment", "brandPillars", "factorScore"];
const METRIC_LABELS = {
  visibility: "Visibility",
  sentiment: "Sentiment",
  brandPillars: "Brand Pillars",
  factorScore: "Factor Score",
};

const COMPOSITE_METRIC_RANGES = {
  visibility: "0-50",
  sentiment: "0-50",
  brandPillars: "0-10",
  factorScore: "0-10",
};
const DIRECT_METRIC_RANGES = {
  visibility: "0-50",
  sentiment: "0-50",
  brandPillars: "0-10",
  factorScore: "0-100",
};

const DIRECT_ROW_KEY = "__direct__";


export default function CompositeMetricsComparison() {
  const discoveryCtx = useContext(DiscoveryContext);
  const { customIndexScoreType, brand_scores, core_attributes, competitors } = discoveryCtx;
  const [selectedFactorKey, setSelectedFactorKey] = useState(null);

  if (!isCompositeIndexScoreType(customIndexScoreType)) {
    return null;
  }

  if (!brand_scores?.length) {
    return null;
  }

  // Get brands from brand_scores (which has main brand at [0] and competitors at [1..n])
  const brands = brand_scores.slice(0, 11).map((bs) => bs.name).filter(Boolean);
  if (!brands.length) return null;

  const getDomainForBrand = (brandName) => {
    const competitor = competitors?.find((c) => c.name?.toLowerCase().trim() === brandName.toLowerCase().trim());
    return competitor?.domain ? extractDomain(competitor.domain) : null;
  };

  const getFavorabilitiesForBrand = (index) => {
    const competitorBrandScore = brand_scores[index];
    return competitorBrandScore?.extras?.favorabilities || [];
  };

  const getRankDataForBrand = (index) => {
    const competitorBrandScore = brand_scores[index];
    return competitorBrandScore?.extras?.rank || [];
  };

  const getSentimentScoreByFactorForBrand = (brandName, index) => {
    return getSentimentScoreByFactor(brandName, getFavorabilitiesForBrand(index));
  };

  const getBrandPillarsDetailForBrand = (brandName) => {
    return getBrandPillarsDetail(brandName, core_attributes) || [];
  };

  const getRankRowForBrand = (brandName, index) => {
    const allRankData = getRankDataForBrand(index);
    const row = allRankData.find(
      (r) => (r.name || "").toLowerCase().trim() === brandName.toLowerCase().trim()
    );
    return row || allRankData[0];
  };

  const getBrandComputation = (brandName, index) => {
    const sentimentScoreByFactor = getSentimentScoreByFactorForBrand(brandName, index);
    if (!sentimentScoreByFactor) return null;

    const productMappingAttribute = brand_scores[index]?.extras?.productMappingAttribute;
    const fullOrderedFactors = getOrderedAccessibilityFactors(sentimentScoreByFactor, productMappingAttribute);
    if (!fullOrderedFactors.length) return null;

    const rankRow = getRankRowForBrand(brandName, index);
    const brandPillarsDetail = getBrandPillarsDetailForBrand(brandName);
    const compositeFactors = filterCompositeFactors(fullOrderedFactors);

    const compositeResult = calcMultiFactorCompositeScore({
      factors: compositeFactors,
      getRankForFactor: (factor) => rankRow?.rankScoreByFactor?.[factor]?.score ?? null,
      getSentimentForFactor: (factor) => sentimentScoreByFactor?.[factor]?.normalizedScore ?? null,
      getPillarForFactor: (factor) => brandPillarsDetail.find((a) => a.name === factor)?.score ?? null,
    });

    const directScore = calcDirectIndexScoreForEntity({
      rankScoreByFactor: rankRow?.rankScoreByFactor,
      sentimentScoreByFactor,
      brandPillarsDetail,
    });
    const hasDirectData = directScore.rankVariants.length > 0 || directScore.sentimentFactor || directScore.pillarFactor;

    return { fullOrderedFactors, compositeBreakdown: compositeResult.breakdown, directScore: hasDirectData ? directScore : null };
  };

  // Row order/labels are driven by the main brand (index 0), same
  // convention AccessibilityMetricsComparison uses for its column order.
  const mainComputation = getBrandComputation(brands[0], 0);
  if (!mainComputation) return null;

  const rowFactors = [];
  let directRow = null;
  mainComputation.fullOrderedFactors.forEach((factor) => {
    if (isExcludedCompositeFactor(factor)) {
      if (!directRow) {
        directRow = {
          key: DIRECT_ROW_KEY,
          label: mainComputation.directScore?.factorLabel || factor,
          isDirect: true,
        };
      }
    } else {
      rowFactors.push({ key: factor, label: factor, isDirect: false });
    }
  });
  if (directRow) rowFactors.unshift(directRow);
  if (!rowFactors.length) return null;

  const activeFactorKey = rowFactors.some((r) => r.key === selectedFactorKey)
    ? selectedFactorKey
    : rowFactors[0].key;
  const activeRow = rowFactors.find((r) => r.key === activeFactorKey) ?? rowFactors[0];

  // Build data structure: factorKey -> brand -> metric -> value
  const buildBrandData = () => {
    const data = {};
    brands.forEach((brandName, brandIdx) => {
      const computation = brandIdx === 0 ? mainComputation : getBrandComputation(brandName, brandIdx);
      rowFactors.forEach((row) => {
        if (!data[row.key]) data[row.key] = {};
        if (row.isDirect) {
          const d = computation?.directScore;
          data[row.key][brandName] = d
            ? {
                // Rounded — this is an average across visibility variants
                // (averageDirectRankAcrossVariants), so it's rarely a whole
                // number otherwise.
                visibility: d.visRaw != null ? Math.round(d.visRaw) : null,
                sentiment: d.sentRaw,
                brandPillars: d.pillarRaw != null ? Number(d.pillarRaw).toFixed(2) : null,
                factorScore: d.total,
                variantCount: d.rankVariants?.length || 0,
              }
            : null;
        } else {
          const f = computation?.compositeBreakdown?.find((b) => b.factor === row.key);
          data[row.key][brandName] = f
            ? {
                visibility: f.visRaw != null ? Math.round(f.visRaw) : null,
                sentiment: f.sentRaw,
                brandPillars: f.pillarRaw != null ? Number(f.pillarRaw).toFixed(2) : null,
                factorScore: f.total,
              }
            : null;
        }
      });
    });
    return data;
  };

  const brandData = buildBrandData();

  const activeRanges = activeRow.isDirect ? DIRECT_METRIC_RANGES : COMPOSITE_METRIC_RANGES;

  return (
    <Box className={classes.container}>
      <div className={classes.titleRow}>
        <h5 className={classes.sectionTitle}>Direct and Composite Metrics Comparison</h5>
        <div className={classes.factorPickerSelect}>
          <MUISelect
            fullWidth
            optionValue={activeFactorKey}
            options={rowFactors.map((r) => ({ name: r.isDirect ? `${r.label} (Direct)` : r.label, value: r.key }))}
            onChange={(e) => setSelectedFactorKey(e.target.value)}
          />
        </div>
      </div>

      <Box className={classes.chartCard}>
        <div className={classes.chartTitleRow}>
          <span className={classes.chartTitle}>{activeRow.label}</span>
          {activeRow.isDirect && <span className={classes.directBadge}>Direct</span>}
        </div>
        <div className={classes.chartGrid}>
          {METRICS.map((metric) => {
            const isScoreCell = metric === "factorScore";
            const max = isScoreCell ? (activeRow.isDirect ? 100 : 10) : metric === "brandPillars" ? 10 : 50;
            return (
              <div key={metric} className={classes.chartMetricBlock}>
                <div className={classes.chartMetricHeader}>
                  <span className={classes.chartMetricLabel}>{METRIC_LABELS[metric]}</span>
                  <span className={classes.chartMetricRange}>{activeRanges[metric]}</span>
                </div>
                <div className={classes.chartBars}>
                  {brands
                    .map((brandName, brandIdx) => {
                      const raw = brandData[activeRow.key]?.[brandName]?.[metric];
                      return { brandName, brandIdx, value: raw == null ? null : Number(raw) };
                    })
                    .sort((a, b) => {
                      if (a.value == null) return 1;
                      if (b.value == null) return -1;
                      return b.value - a.value;
                    })
                    .map(({ brandName, brandIdx, value }) => {
                    const pct = value == null ? 0 : Math.max(0, Math.min(100, (value / max) * 100));
                    const display = value == null
                      ? "—"
                      : isScoreCell
                        ? value.toFixed(1)
                        : metric === "brandPillars"
                          ? value.toFixed(2)
                          : value;
                    const domain = getDomainForBrand(brandName);
                    return (
                      <div key={brandName} className={classes.chartBarRow}>
                        <Tooltip title={brandName} placement="top">
                          <span className={classes.chartBrandLabel}>
                            <span className={classes.chartBrandLogo}>
                              <BrandAvatar domainName={domain} brandName={brandName} />
                            </span>
                            <span className={classes.chartBrandName}>{brandName}</span>
                          </span>
                        </Tooltip>
                        <span className={classes.chartTrack} style={{ background: INDEX_TRACK }}>
                          <span
                            className={classes.chartFill}
                            style={{ width: `${pct}%`, background: INDEX_ACCENT, opacity: brandIdx === 0 ? 1 : 0.5 }}
                          />
                        </span>
                        <span className={classes.chartValue} style={{ color: brandIdx === 0 ? INDEX_TEXT : INDEX_TEXT_MUTED }}>
                          {display}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </Box>
    </Box>
  );
}
